export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  githubUsername?: string;
  avatarUrl?: string | null; // profile picture from Keycloak, when it sends one
  createdAt: string;
  lastLoginAt?: string;
  updatedAt?: string;
  
  // Personal details
  mothersName?: string;
  fathersName?: string;
  
  // Contact details
  phone?: string;
  permanentAddress?: string;
  currentAddress?: string;
  
  // Bank account details
  accountNumber?: string;
  ifsc?: string;
  accountHolderName?: string;
  
  // KYC details
  aadharNumber?: string;
  panNumber?: string;
}

export enum UserRole {
  SUPERUSER = 'SUPERUSER',
  ADMIN = 'ADMIN',
  DEVOPS = 'DEVOPS',
  DEVELOPER = 'DEVELOPER',
  AUDITOR = 'AUDITOR'
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

export enum AccessStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export type AccessType = 'READ' | 'WRITE';

export interface Service {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  environments?: Environment[];
  _count: {
    environments: number;
    variables: number;
  };
}

export interface Environment {
  id: string;
  name: string;
  description?: string;
  disabled?: boolean;
  createdAt: string;
  updatedAt: string;
  hasAccess?: boolean;
  accessType?: AccessType;
  _count: {
    variables: number;
  };
}

export interface EnvironmentVariable {
  id: string;
  key: string;
  value: string;
  isSecret: boolean;
  isBuildTime: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  createdBy: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface ServiceAccessRequest {
  id: string;
  accessType: AccessType;
  status: AccessStatus;
  reason?: string;
  requestedAt: string;
  createdAt: string;
  respondedAt?: string;
  environment: {
    id: string;
    name: string;
    service: {
      id: string;
      name: string;
      description?: string;
    };
  };
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
  };
  approvedBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface VariableHistory {
  id: string;
  key: string;
  value: string;
  isSecret: boolean;
  version: number;
  changedAt: string;
  changedBy: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface GitHubAccessRequest {
  id: string;
  repository: string;
  accessType: AccessType;
  status: AccessStatus;
  reason?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    githubUsername?: string;
    role: UserRole;
  };
  approvedBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  githubStatus?: {
    success: boolean;
    error?: string;
  };
}

export enum DeboardingStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
}

export enum DeboardingPhase {
  CUT_APP_ACCESS = 'CUT_APP_ACCESS',
  GITHUB_ACCESS_REVOCATION = 'GITHUB_ACCESS_REVOCATION',
  SERVICE_ACCESS_REVOCATION = 'SERVICE_ACCESS_REVOCATION',
  USER_ACCOUNT_DEACTIVATION = 'USER_ACCOUNT_DEACTIVATION',
  AUDIT_LOG_CLEANUP = 'AUDIT_LOG_CLEANUP'
}

export interface DeboardingProcess {
  id: string;
  status: DeboardingStatus;
  reason?: string;
  initiatedBy: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    status: UserStatus;
    githubUsername?: string;
  };
  initiator: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  phaseLogs: DeboardingPhaseLog[];
}

export interface DeboardingPhaseLog {
  id: string;
  phase: DeboardingPhase;
  status: DeboardingStatus;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  details?: any;
}

export enum CpuAllocation {
  REQUEST_BASED = 'REQUEST_BASED',
  INSTANCE_BASED = 'INSTANCE_BASED'
}

// Where an environment deploys. Wisp is available for development only.
export enum DeployTarget {
  CLOUD_RUN = 'CLOUD_RUN',
  WISP = 'WISP',
  BOTH = 'BOTH'
}

export interface RuntimeConfigFields {
  cpu?: number | null;
  memory?: string | null;
  port?: number | null;
  concurrency?: number | null;
  minInstances?: number | null;
  maxInstances?: number | null;
  vpcConnector?: string | null;
  authType?: 'unauthenticated' | 'iam' | null;
  region?: string | null;
  cpuAllocation?: CpuAllocation | null;
  deployTarget?: DeployTarget | null; // env overrides only; null = default (Both for development)
}

export interface ServiceRuntimeConfig extends RuntimeConfigFields {
  id: string;
  serviceId: string;
  updatedAt: string;
  createdAt: string;
}

export interface ServiceEnvOverride extends RuntimeConfigFields {
  id: string;
  serviceId: string;
  environment: 'DEV' | 'PROD';
  updatedAt: string;
  createdAt: string;
}

export interface Deployment {
  id: string;
  repo: string;
  serviceName: string;
  environment: 'DEV' | 'PROD';
  commitSha: string;
  imageDigest?: string;
  status: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
  logsUrl?: string;
  buildId?: string;
  createdAt: string;
  updatedAt: string;
  phases?: {
    clone: 'pending' | 'running' | 'completed' | 'failed';
    build: 'pending' | 'running' | 'completed' | 'failed';
    deploy: 'pending' | 'running' | 'completed' | 'failed';
  };
}

// Mirrors Herald's store.Task (see herald-by-small-group/internal/store),
// as proxied by /api/herald/tasks.
export interface HeraldTask {
  task_id: string;
  repo: string;
  github_repo: string;
  branch: string;
  base_branch: string;
  prompt: string;
  triggered_by: string;
  preview_url: string;
  pr_url: string;
  status: 'running' | 'waiting_for_input' | 'succeeded' | 'failed' | 'cancelled';
  failure_reason: string;
  created_at: number; // unix seconds
  cleaned_at: number; // unix seconds, 0 if never cleaned
}

export type FileVisibility = 'PUBLIC' | 'PRIVATE';

export interface StoredFile {
  id: string;
  namespace: string;
  filename: string;
  contentType: string;
  size: number;
  visibility: FileVisibility;
  status: 'PENDING' | 'READY';
  uploadedById: string | null;
  uploadedByKeyId: string | null;
  uploadedBy?: string | null; // email or API key name
  createdAt: string;
}

export interface ServiceApiKey {
  id: string;
  name: string;
  namespace: string;
  prefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  createdBy: { email: string } | null;
}
